import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaCode, FaServer, FaDatabase, FaMobile, FaTools, FaCloud, FaChevronDown } from "react-icons/fa";
import ScrollDown from "./ScrollDown";
import { skillCategories } from "../data/skills";

const CATEGORY_KEYS = ['frontend', 'backend', 'database', 'mobile', 'toolsDesign', 'devopsCloud'];

const CATEGORY_ICONS = [
  <FaCode className="w-4 h-4 sm:w-5 sm:h-5" key="frontend" />,
  <FaServer className="w-4 h-4 sm:w-5 sm:h-5" key="backend" />,
  <FaDatabase className="w-4 h-4 sm:w-5 sm:h-5" key="database" />,
  <FaMobile className="w-4 h-4 sm:w-5 sm:h-5" key="mobile" />,
  <FaTools className="w-4 h-4 sm:w-5 sm:h-5" key="toolsDesign" />,
  <FaCloud className="w-4 h-4 sm:w-5 sm:h-5" key="devopsCloud" />,
];

const SkillCard = ({ title, logo }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -12, scale: 1.05 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="bg-primary/30 p-3 sm:p-4 rounded-xl border border-secondary/20 hover:border-secondary/60
                transition-all duration-500 hover:shadow-2xl hover:shadow-secondary/30 w-full h-full min-w-0
                cursor-pointer relative overflow-hidden backdrop-blur-sm"
    >
      {/* Animated background particles */}
      <motion.div
        className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        initial={false}
        animate={{ opacity: isHovered ? 1 : 0 }}
      >
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-secondary rounded-full"
            initial={{ x: "50%", y: "50%", scale: 0 }}
            animate={{
              x: ["50%", `${Math.random() * 80 + 10}%`],
              y: ["50%", `${Math.random() * 80 + 10}%`],
              scale: [0, 1, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
              delay: i * 0.2,
              ease: "easeInOut",
            }}
          />
        ))}
      </motion.div>

      {/* Hover gradient overlay */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-secondary/10 via-transparent to-primary/10 opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        initial={false}
        animate={{ opacity: isHovered ? 1 : 0 }}
      />

      <div className="relative z-10 flex flex-col items-center space-y-2 sm:space-y-3 w-full min-w-0">
        <div className="relative group w-10 h-10 xs:w-11 xs:h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center flex-shrink-0">
          {!imageLoaded && logo && (
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 to-secondary/40 rounded-xl border border-secondary/40 animate-pulse" />
          )}
          {logo ? (
          <motion.img
            src={logo}
            alt={`${title} logo`}
            className={`w-10 h-10 xs:w-11 xs:h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 object-contain filter brightness-90 group-hover:brightness-100
                       transition-all duration-500 transform group-hover:scale-125 group-hover:rotate-6 ${
                         imageLoaded ? "opacity-100" : "opacity-0"
                       }`}
            whileHover={{
              rotate: [0, -5, 5, 0],
              scale: [1, 1.1, 1.05, 1.15],
            }}
            transition={{ duration: 0.6 }}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageLoaded(true)}
            loading="lazy"
            decoding="async"
          />
          ) : (
            <div className="w-10 h-10 xs:w-11 xs:h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-secondary/20 text-secondary text-xs font-bold">
              {title.slice(0, 2).toUpperCase()}
            </div>
          )}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-secondary/20 via-primary/20 to-secondary/20 rounded-full blur-2xl opacity-0
                        group-hover:opacity-100 transition-all duration-500 pointer-events-none"
            animate={{ scale: isHovered ? [1, 1.2, 1] : 1 }}
            transition={{
              duration: 2,
              repeat: isHovered ? Infinity : 0,
              repeatType: "reverse",
            }}
          />
        </div>

        <motion.h3
          className="w-full text-xs sm:text-sm font-medium text-textPrimary group-hover:text-secondary transition-all duration-500 text-center leading-tight break-words px-1"
          whileHover={{
            scale: 1.05,
            textShadow: "0 0 10px rgba(100, 255, 218, 0.5)",
          }}
          transition={{ duration: 0.3 }}
        >
          {title}
        </motion.h3>
      </div>
    </motion.div>
  );
};

const SkillSection = ({ skills }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.2 }}
    className="skills-grid w-full grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5
               gap-3 xs:gap-4 sm:gap-5 md:gap-6 lg:gap-8 auto-rows-fr"
  >
    {skills.map((skill, index) => (
      <motion.div
        key={skill.title}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.6,
          delay: 0.1 + index * 0.05,
          type: "spring",
          stiffness: 100,
        }}
        className="w-full min-w-0"
      >
        <SkillCard title={skill.title} logo={skill.logo} />
      </motion.div>
    ))}
  </motion.div>
);

const Skills = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const categoryMeta = useMemo(
    () =>
      CATEGORY_KEYS.map((key, index) => ({
        icon: CATEGORY_ICONS[index],
        label: t(`skills.categories.${key}.label`),
        description: t(`skills.categories.${key}.description`),
      })),
    [t],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const selectCategory = (index) => {
    setActiveTab(index);
    setDropdownOpen(false);
  };

  return (
    <section id="skills" className="section-padding relative w-full max-w-full overflow-x-clip">
      <motion.div
        className="w-full max-w-full px-3 xs:px-4 md:px-8 lg:px-16"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <motion.h2
          className="heading text-center mb-8 sm:mb-12"
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
        >
          {t('skills.title')}
        </motion.h2>

        <div className="w-full min-w-0">
          {/* Mobile / tablet category dropdown */}
          <div className="lg:hidden mb-4 sm:mb-6 relative z-30" ref={dropdownRef}>
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              aria-controls="skills-category-menu"
              onClick={() => setDropdownOpen((open) => !open)}
              className="skills-category-trigger w-full flex items-center justify-between gap-3 min-h-[48px] px-4 py-3 rounded-xl border border-secondary/30 bg-primary/80 text-left text-secondary font-semibold text-sm sm:text-base shadow-lg shadow-secondary/10 backdrop-blur-sm"
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className="shrink-0">{categoryMeta[activeTab].icon}</span>
                <span className="truncate">{categoryMeta[activeTab].label}</span>
              </span>
              <FaChevronDown
                className={`w-4 h-4 shrink-0 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            <AnimatePresence>
              {dropdownOpen && (
                <motion.ul
                  id="skills-category-menu"
                  role="listbox"
                  aria-label={t('skills.categoriesAriaLabel')}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 right-0 top-full mt-2 max-h-[min(320px,60vh)] overflow-y-auto rounded-xl border border-secondary/25 bg-primary/95 backdrop-blur-xl shadow-2xl shadow-black/40 py-2 z-40"
                >
                  {skillCategories.map((cat, index) => (
                    <li key={CATEGORY_KEYS[index]} role="option" aria-selected={activeTab === index}>
                      <button
                        type="button"
                        onClick={() => selectCategory(index)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm sm:text-base transition-colors min-h-[44px] ${
                          activeTab === index
                            ? 'bg-secondary/15 text-secondary font-semibold'
                            : 'text-textSecondary hover:bg-secondary/10 hover:text-secondary'
                        }`}
                      >
                        <span className="shrink-0">{categoryMeta[index].icon}</span>
                        <span className="truncate">{categoryMeta[index].label}</span>
                      </button>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 w-full">
            {/* Desktop sidebar tabs */}
            <motion.div
              className="hidden lg:flex lg:w-64 flex-shrink-0 flex-col w-full"
              role="tablist"
              aria-label={t('skills.categoriesAriaLabel')}
            >
              {skillCategories.map((cat, index) => (
                <button
                  key={CATEGORY_KEYS[index]}
                  type="button"
                  role="tab"
                  id={`skill-tab-${index}`}
                  aria-selected={activeTab === index}
                  aria-controls={`skill-panel-${index}`}
                  onClick={() => setActiveTab(index)}
                  className={`relative flex items-center px-4 py-3 text-sm font-medium text-left border-l-4 whitespace-nowrap transition-all duration-300 rounded-lg mb-1 w-full overflow-hidden group
                    ${
                      activeTab === index
                        ? "text-secondary border-secondary bg-secondary/10 shadow-lg shadow-secondary/20"
                        : "text-textSecondary border-transparent hover:text-secondary hover:bg-secondary/5"
                    }`}
                >
                  {activeTab === index && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-secondary/5 to-transparent pointer-events-none"
                      initial={{ x: "-100%" }}
                      animate={{ x: "100%" }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                    />
                  )}
                  <motion.span
                    className="mr-2 transition-transform duration-300 group-hover:scale-110 flex-shrink-0"
                    whileHover={{ rotate: 15 }}
                  >
                    {categoryMeta[index].icon}
                  </motion.span>
                  <span className="truncate relative z-10">{categoryMeta[index].label}</span>
                </button>
              ))}
            </motion.div>

            {/* Tab Content */}
            <div className="flex-1 lg:pl-4 min-w-0 w-full">
              {skillCategories.map((cat, index) =>
                activeTab === index ? (
                  <motion.div
                    key={CATEGORY_KEYS[index]}
                    role="tabpanel"
                    id={`skill-panel-${index}`}
                    aria-labelledby={`skill-tab-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="space-y-4 sm:space-y-6 w-full min-w-0"
                  >
                    <motion.div
                      className="flex items-center mb-2 min-w-0"
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <motion.span
                        className="text-xl sm:text-2xl text-secondary mr-2 sm:mr-3 flex-shrink-0"
                        whileHover={{ scale: 1.2, rotate: 10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {categoryMeta[activeTab].icon}
                      </motion.span>
                      <motion.h3
                        className="text-xl sm:text-2xl font-semibold text-secondary truncate"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.3 }}
                      >
                        {categoryMeta[activeTab].label}
                      </motion.h3>
                      <motion.div
                        className="flex-1 ml-3 sm:ml-4 h-0.5 bg-secondary/60 origin-left hidden sm:block"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.8, delay: 0.4, type: "spring" }}
                      />
                    </motion.div>

                    <motion.p
                      className="text-textSecondary mb-4 sm:mb-6 text-xs sm:text-sm md:text-base"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                    >
                      {categoryMeta[activeTab].description}
                    </motion.p>

                    <SkillSection skills={cat.skills} />
                  </motion.div>
                ) : null
              )}
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-1/2 right-0 -z-10 pointer-events-none">
          <motion.div
            className="blur-decoration w-64 h-64 sm:w-96 sm:h-96 bg-secondary/5 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
        </div>
      </motion.div>

      <ScrollDown targetId="education" inline />
    </section>
  );
};

export default Skills;
